<template>
    <div class="sailings-dates">
        <ul class="sailings-dates__list">
            <li
                v-for="(visibleDate, index) in visibleDates"
                @click="activeDate = visibleDate"
                :key="index"
                class="sailings-dates__date"
                :class="{ active: YYYYMMDD(visibleDate) === YYYYMMDD(activeDate) }"
            >
                <div class="top">
                    <div class="date-text day">{{ DD(visibleDate) }}</div>
                    <small class="date-text weekday">{{ dayOfWeekName(visibleDate, 3) }}</small>
                </div>
                <div class="date-text month">{{ monthName(visibleDate, 3) }}</div>
            </li>
        </ul>
        <h4>
            {{ fullDate(activeDate) }}
        </h4>
    </div>
</template>

<script lang="ts" setup>
import { YYYYMMDD, DD, dayOfWeekName, fullDate, monthName } from "@/helpers/dateHelpers";
import { addDays, eachDayOfInterval, startOfToday } from "date-fns";
import { computed } from "vue";

interface Props {
    modelValue: Date;
}

const props = defineProps<Props>();
const emit = defineEmits(["update:modelValue"]);

const activeDate = computed({
    get(): Date {
        return props.modelValue;
    },
    set(date: Date) {
        emit("update:modelValue", date);
    },
});

const visibleDates = computed(() => {
    return eachDayOfInterval({
        start: addDays(startOfToday(), -1),
        end: addDays(startOfToday(), 5),
    });
});
</script>

<style lang="scss" scoped>
.sailings-dates {
    display: flex;
    flex-direction: column;

    @media print {
        box-shadow: none;
    }

    &__list {
        display: flex;
        flex: auto;
        gap: 5px;
        border-bottom: 15px var(--c-primary) solid;
        padding: 0;
    }

    h4 {
        color: var(--c-primary);
        margin-top: 40px;
    }

    &__date {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;

        flex: 0 1 100%;
        border-radius: 5px 5px 0 0;
        background-color: #ececec;
        height: 70px;
        padding: 10px 0px;

        &:hover {
            background-color: darken(#ececec, 10%);
        }

        .top {
            display: flex;
            gap: 5px;
        }

        .date-text {
            color: #989898;

            &.day,
            &.month {
                font-weight: bold;
            }

            &.day,
            &.month,
            &.weekday {
                line-height: 1;
            }

            &.weekday {
                padding: 2px 0;
            }

            &.day {
                font-size: 1.25rem;
            }
        }

        &.active {
            background-color: var(--c-primary);

            .date-text {
                color: var(--c-base);
            }
        }

        &.disabled {
            opacity: 0.5;
        }
    }
}
</style>
